import { Typography } from '@components/style/Typography';
import { type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import { useServicePortfolioFilter } from '../../../../hooks/service/UseServicePortfolioFilter';
import { usePortfolioFilter } from '../../../../hooks/UsePortfolioFilter';
import { AppState } from '../../../../store/AppState';
import type { PortfolioState } from '../../../../store/portfolio/PortfolioReducer';
import { setRightMobileLayout } from '../../../../store/service-portfolio/ServiceActions';
import type { ServiceState } from '../../../../store/service-portfolio/ServiceReducer';

export type HeaderLayoutProps = {
  type: 'loyalty' | 'service';
};

export const HeaderLayout: FC<HeaderLayoutProps> = ({ type }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const isLoyalty = type === 'loyalty';
  const headerText = isLoyalty ? 'Loyalty Portfolio' : 'Service Portfolio';
  const dataTestId = isLoyalty ? 'portfolio' : 'service-portfolio';
  const headerBlockDataTestId = `${dataTestId}-header-block`;
  const filterButtonDataTestId = `${dataTestId}-filter`;
  const filterButtonIconDataTestId = `${dataTestId}-filter-icon`;

  const { dealType } = useSelector<AppState, PortfolioState>(
    (portfolioState) => portfolioState.portfolio,
  );
  const { selectedPortfolioType } = useSelector<AppState, ServiceState>((state) => state.service);

  const { hasFilters: hasPortfolioFilters } = usePortfolioFilter();
  const { hasFilters: hasServiceFilters } = useServicePortfolioFilter();
  const hasFilters =
    (isLoyalty && hasPortfolioFilters(dealType)) ||
    (!isLoyalty && hasServiceFilters(selectedPortfolioType));

  return (
    <HeaderBlock data-testid={headerBlockDataTestId}>
      <Typography
        theme={theme}
        size={theme.typography.h3}
        fontWeight={theme.typography.fontWeights.semibold}
      >
        {headerText}
      </Typography>
      <FilterButton
        data-testid={filterButtonDataTestId}
        onClick={() => dispatch(setRightMobileLayout(true))}
      >
        <FilterIcon
          data-testid={filterButtonIconDataTestId}
          className='am-icon am-icon-filter'
        ></FilterIcon>
        Filters
        {hasFilters && (
          <FilterIndicatorIcon data-testid='filter-indicator-icon'>
            <sup>&bull;</sup>
          </FilterIndicatorIcon>
        )}
      </FilterButton>
    </HeaderBlock>
  );
};

const HeaderBlock = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.palette.backgroundGray};
  padding: 10px 12px;
  justify-content: space-between;
  place-items: baseline;
`;

const FilterButton = styled.button`
  font-size: larger;
  padding: 10px 14px 8px 8px;
  height: fit-content;
  border: 1px solid ${({ theme }) => theme.palette.grayTwo};
  border-radius: 3px;
  color: ${({ theme }) => theme.palette.black} !important;
`;

const FilterIcon = styled.span`
  font-size: 100%;
  vertical-align: bottom;
  margin-right: 5px;
`;

const FilterIndicatorIcon = styled.span`
  font-size: xxx-large;
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.palette.linkColor};
  position: absolute;
  padding-top: 2px;
`;
